import React, { useState, useEffect } from 'react'
import { Settings as SettingsIcon, DollarSign, Users, Key, Save, Plus, AlertCircle, CheckCircle, Mail, Sparkles, Shield, Play, Download, Info, Trophy, Eye } from 'lucide-react'
import { settingsAPI, huntAPI, sessionAPI, gdprAPI, authAPI, settingsTemplateAPI } from '../services/api'
import certificateAPI from '../services/certificateAPI'
import CertificatePreviewModal from '../components/CertificatePreviewModal'
import toast from 'react-hot-toast'
import { useNavigate, useLocation } from 'react-router-dom'

const Settings = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const pathParts = location.pathname.split('/')
  const urlTab = pathParts.length > 2 ? pathParts[2] : 'manual-codes'

  const [activeTab, setActiveTab] = useState('manual-codes')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  
  // GDPR state
  const [gdprLogs, setGdprLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [runType, setRunType] = useState('daily')
  
  // Manual code state
  const [hunts, setHunts] = useState([])
  const [manualCodeForm, setManualCodeForm] = useState({
    hunt_id: '',
    customer_email: '',
    customer_name: '',
    access_code: '',
    booking_reference: ''
  })
  const [creatingCode, setCreatingCode] = useState(false)
  
  // System settings state
  const [systemSettings, setSystemSettings] = useState({})
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    sendgrid_api_key: '',
    from_email: '',
    from_name: '',
    email_template_subject: '',
    email_template_body: ''
  })
  const [savingEmail, setSavingEmail] = useState(false)

  // Completion email settings state
  const [completionEmailSettings, setCompletionEmailSettings] = useState({
    subject: '',
    body: '',
    text_body: ''
  })
  
  // Certificate test state
  const [testCertificateData, setTestCertificateData] = useState({
    userName: 'Test User',
    huntName: 'Central Park Classic',
    city: 'New York',
    userEmail: 'test@example.com',
    completedAt: new Date().toISOString(),
    startedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    totalAttempts: 12,
    totalLocationsVisited: 10,
    completionHour: new Date().getHours(),
    completionDay: new Date().getDay(),
    totalUserCompletions: 1,
    isSpeedDemon: false,
    isPerfectScore: false,
    isNightOwl: false,
    isWeekendWarrior: false,
    isEarlyBird: false,
    isStreakMaster: false,
    isExplorerLegend: false
  })
  const [testCertificateLoading, setTestCertificateLoading] = useState(false)
  const [testCertificateResult, setTestCertificateResult] = useState(null)
  const [savingCompletionEmail, setSavingCompletionEmail] = useState(false)
  
  // Certificate preview state
  const [certificatePreviewOpen, setCertificatePreviewOpen] = useState(false)
  const [certificatePreviewHTML, setCertificatePreviewHTML] = useState(null)
  const [certificatePreviewLoading, setCertificatePreviewLoading] = useState(false)
  const [certificatePreviewPrizes, setCertificatePreviewPrizes] = useState([])

  useEffect(() => {
    loadHunts()
    loadSystemSettings()
    loadEmailSettings()
    loadCompletionEmailSettings()
  }, [])
  
  useEffect(() => {
    if (urlTab && urlTab !== activeTab) setActiveTab(urlTab)
  }, [urlTab, activeTab])
  
  useEffect(() => {
    if (activeTab === 'gdpr') fetchGdprLogs()
  }, [activeTab])
  
  const handleTabChange = (newTab) => {
    setActiveTab(newTab)
    navigate(`/settings/${newTab}`)
  }

  const fetchGdprLogs = async () => {
    try {
      setLogsLoading(true)
      const res = await gdprAPI.getLogs({ limit: 50 })
      if (res.success) setGdprLogs(res.data)
    } catch (e) {
      console.error(e)
      toast.error('Failed to load GDPR logs')
    } finally {
      setLogsLoading(false)
    }
  }

  const handleRunGdpr = async () => {
    const confirmed = window.confirm(`Run GDPR cleanup now (${runType})?`)
    if (!confirmed) return
    try {
      setLoading(true)
      const res = await gdprAPI.runNow(runType)
      if (res.success) {
        toast.success(`${runType} GDPR cleanup started`) 
        fetchGdprLogs()
      } else {
        toast.error('Failed to start GDPR cleanup')
      }
    } catch (e) {
      console.error(e)
      toast.error('Failed to start GDPR cleanup')
    } finally {
      setLoading(false)
    }
  }

  const downloadLogsCSV = () => {
    const header = ['id','type','created_at','duration']
    const rows = gdprLogs.map(l => [l.id, l.type || l.cleanup_type, l.created_at, l.duration])
    const csv = [header.join(','), ...rows.map(r => r.join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `gdpr_logs_${new Date().toISOString().slice(0,10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const loadHunts = async () => {
    try {
      const response = await huntAPI.getAll()
      if (response.success) setHunts(response.data.filter(h => h.is_active))
    } catch (error) {
      console.error('Error loading hunts:', error)
      toast.error('Failed to load hunts')
    }
  }
  
  const loadSystemSettings = async () => {
    try {
      const response = await settingsAPI.getAll()
      if (response.success) setSystemSettings(response.data)
    } catch (error) {
      console.error('Error loading system settings:', error)
    }
  }
  
  const loadEmailSettings = async () => {
    try {
      const response = await settingsAPI.getEmailSettings()
      if (response.success) setEmailSettings(response.data)
    } catch (error) {
      console.error('Error loading email settings:', error)
    }
  }

  const loadCompletionEmailSettings = async () => {
    try {
      const response = await settingsAPI.getCompletionEmailSettings()
      if (response.success) setCompletionEmailSettings(response.data)
    } catch (error) {
      console.error('Error loading completion email settings:', error)
    }
  }

  const handleManualCodeSubmit = async (e) => {
    e.preventDefault()
    try {
      setCreatingCode(true)
      const response = await sessionAPI.createManual(manualCodeForm)
      if (response.success) {
        toast.success('Manual code created successfully')
        setManualCodeForm({ hunt_id:'', customer_email:'', customer_name:'', access_code:'', booking_reference:'' })
      } else {
        toast.error('Failed to create manual code')
      }
    } catch (error) {
      console.error('Error creating manual code:', error)
      toast.error('Failed to create manual code')
    } finally { setCreatingCode(false) }
  }

  const handleEmailSettingsSubmit = async (e) => {
    e.preventDefault()
    try {
      setSavingEmail(true)
      const response = await settingsAPI.updateEmailSettings(emailSettings)
      if (response.success) toast.success('Email settings updated successfully')
      else toast.error('Failed to update email settings')
    } catch (error) {
      console.error('Error updating email settings:', error)
      toast.error('Failed to update email settings')
    } finally { setSavingEmail(false) }
  }

  const testEmailConfiguration = async () => {
    try {
      const response = await settingsAPI.sendTestEmail(emailSettings)
      if (response.success) {
        toast.success('Test email sent successfully')
      } else {
        toast.error('Failed to send test email')
      }
    } catch (error) {
      console.error('Error testing email configuration:', error)
      toast.error('Failed to send test email')
    }
  }

  const testEmailTemplate = async () => {
    try {
      const res = await settingsTemplateAPI.sendTemplateTest({})
      if (res.success) toast.success('Template test email sent')
      else toast.error('Failed to send template test email')
    } catch (e) {
      console.error(e)
      toast.error('Failed to send template test email')
    }
  }

  const handleCompletionEmailSettingsSubmit = async (e) => {
    e.preventDefault()
    try {
      setSavingCompletionEmail(true)
      const response = await settingsAPI.updateCompletionEmailSettings(completionEmailSettings)
      if (response.success) toast.success('Completion email settings updated successfully')
      else toast.error('Failed to update completion email settings')
    } catch (error) {
      console.error('Error updating completion email settings:', error)
      toast.error('Failed to update completion email settings')
    } finally { setSavingCompletionEmail(false) }
  }

  const testCompletionEmail = async () => {
    try {
      // Use a real email address for testing
      const testEmail = 'info@secretsofthepath.com' // Change this to your actual email
      
      const response = await settingsAPI.testCompletionEmail({
        userName: 'Test User',
        huntName: 'Test Hunt',
        city: 'Test City',
        userEmail: testEmail
      })
      if (response.success) {
        toast.success(`Test completion email sent successfully to ${testEmail}`)
      } else {
        toast.error('Failed to send test completion email')
      }
    } catch (error) {
      console.error('Error testing completion email:', error)
      toast.error('Failed to send test completion email')
    }
  }

  const testCertificate = async () => {
    try {
      setTestCertificateLoading(true)
      setTestCertificateResult(null)
      
      const response = await certificateAPI.generateTestCertificate(testCertificateData)
      
      if (response.success) {
        setTestCertificateResult(response)
        toast.success('Test certificate generated successfully!')
      } else {
        toast.error('Failed to generate test certificate')
      }
    } catch (error) {
      console.error('Error testing certificate:', error)
      toast.error('Failed to generate test certificate')
    } finally {
      setTestCertificateLoading(false)
    }
  }

  const viewTestCertificate = () => {
    if (testCertificateResult?.certificateLink) {
      window.open(testCertificateResult.certificateLink, '_blank')
    }
  }

  const viewTestEmail = () => {
    if (testCertificateResult?.completionEmailHTML) {
      const newWindow = window.open('', '_blank')
      newWindow.document.write(testCertificateResult.completionEmailHTML)
      newWindow.document.close()
    }
  }

  const previewCertificate = async () => {
    try {
      setCertificatePreviewLoading(true)
      setCertificatePreviewHTML(null)
      setCertificatePreviewPrizes([])
      
      const response = await certificateAPI.generateCertificatePreview(testCertificateData)
      
      if (response.success && response.certificateHTML) {
        setCertificatePreviewHTML(response.certificateHTML)
        setCertificatePreviewPrizes(response.mockPrizes || [])
        setCertificatePreviewOpen(true)
      } else {
        toast.error('Failed to generate certificate preview')
      }
    } catch (error) {
      console.error('Error generating certificate preview:', error)
      toast.error('Failed to generate certificate preview')
    } finally {
      setCertificatePreviewLoading(false)
    }
  }

  // Template helper functions
  const insertVariable = (variable) => {
    const textarea = document.querySelector('textarea[value*="email_template_body"]') || 
                    document.querySelector('textarea[placeholder*="access code"]')
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const text = emailSettings.email_template_body
      const newText = text.substring(0, start) + variable + text.substring(end)
      setEmailSettings(prev => ({ ...prev, email_template_body: newText }))
      
      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + variable.length, start + variable.length)
      }, 0)
    }
  }

  const getDefaultTemplate = () => {
    return `🎯 Your Scavenger Hunt is Ready!

Hi {customer_name},

Your adventure awaits! Here's everything you need to know about your {hunt_name} scavenger hunt:

📋 HUNT DETAILS:
• Hunt Name: {hunt_name}
• Duration: {estimated_duration}
• Difficulty: {difficulty}
• Access Code: {access_code}

🚀 GET STARTED:
1. Visit: {hunt_url}
2. Enter your access code: {access_code}
3. Follow the clues and solve the puzzles
4. Complete your adventure within {expires_in}

⚠️ IMPORTANT: Your access code expires in {expires_in}. Make sure you're ready to start before entering the code!

Need help? Contact us at support@yourdomain.com

Happy hunting! 🎉
- The Scavenger Hunt Team`
  }

  const getSimpleTemplate = () => {
    return `Hi {customer_name},

Your access code for {hunt_name} is: {access_code}

Visit {hunt_url} to start your hunt.

Enjoy your adventure!

Best regards,
The Scavenger Hunt Team`
  }

  const getAdvancedTemplate = () => {
    return `🌟 WELCOME TO YOUR ADVENTURE! 🌟

Dear {customer_name},

🎯 Your {hunt_name} scavenger hunt is ready to begin!

📊 HUNT INFORMATION:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎮 Hunt: {hunt_name}
⏱️ Duration: {estimated_duration}
🎯 Difficulty: {difficulty}
🔑 Access Code: {access_code}
🔗 Start Here: {hunt_url}

🚀 READY TO START?
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
1️⃣ Click the link above or visit: {hunt_url}
2️⃣ Enter your access code: {access_code}
3️⃣ Follow the clues and solve the puzzles
4️⃣ Complete your adventure within {expires_in}

⚠️ IMPORTANT REMINDER:
Your access code expires in {expires_in}. Make sure you're ready to start before entering the code!

🎉 Get ready for an amazing adventure!
The Scavenger Hunt Team

Need help? Contact us at support@yourdomain.com`
  }

  const [admins, setAdmins] = useState([])
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '', is_superadmin: false })

  useEffect(() => {
    if (activeTab === 'admins') loadAdmins()
  }, [activeTab])

  const loadAdmins = async () => {
    try {
      const res = await authAPI.listAdmins()
      if (res.success) setAdmins(res.data)
    } catch (e) { /* noop */ }
  }

  const handleCreateAdmin = async (e) => {
    e.preventDefault()
    try {
      const res = await authAPI.createAdmin(newAdmin.email, newAdmin.password, newAdmin.is_superadmin)
      if (res.success) {
        setNewAdmin({ email: '', password: '', is_superadmin: false })
        loadAdmins()
        toast.success('Admin created')
      } else {
        toast.error(res.error || 'Failed to create admin')
      }
    } catch (e) { toast.error('Failed to create admin') }
  }

  const tabs = [
    { id: 'manual-codes', name: 'Manual Codes', icon: Key },
    { id: 'email', name: 'Email Settings', icon: Mail },
    { id: 'completion-email', name: 'Completion Email', icon: Trophy },
    { id: 'certificate-test', name: 'Certificate Test', icon: Download },
    { id: 'system', name: 'System Settings', icon: SettingsIcon },
    { id: 'gdpr', name: 'GDPR', icon: Shield },
    { id: 'admins', name: 'Admins', icon: Users },
  ]

  return (
    <div className="p-4 lg:p-6 space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-sm lg:text-base text-gray-600">Manage system configuration and preferences</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-1 lg:space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => handleTabChange(tab.id)} className={`py-2 px-2 lg:px-1 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap flex-shrink-0 ${activeTab === tab.id ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
              <tab.icon className="inline-block w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'gdpr' && (
          <div className="space-y-6">
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">GDPR Cleanup</h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Run Type</label>
                  <select value={runType} onChange={(e)=>setRunType(e.target.value)} className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base">
                    <option value="daily">Daily (UK: 3m code wipe, 6m PII)</option>
                    <option value="aggressive">Aggressive (same thresholds, safety net)</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-2">Daily uses strict timing based on completed_at/expired_at. Active/unstarted sessions are never touched.</p>
                </div>
                <div className="flex justify-start lg:justify-center">
                  <button onClick={handleRunGdpr} disabled={loading} className="btn-primary flex items-center">
                    {loading ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Running...</>) : (<><Play className="w-4 h-4 mr-2"/>Run GDPR Cleanup</>)}
                  </button>
                </div>
                <div className="flex justify-start lg:justify-end">
                  <button onClick={downloadLogsCSV} className="btn-secondary flex items-center"><Download className="w-4 h-4 mr-2"/>Download Logs CSV</button>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center mb-3">
                <Info className="w-4 h-4 text-blue-600 mr-2"/>
                <h3 className="text-lg font-semibold text-gray-900">UK GDPR Retention Policy</h3>
              </div>
              <ul className="text-sm text-gray-700 space-y-2">
                <li><span className="font-medium">Access codes</span>: wiped after <span className="font-medium">3 months</span> (completed/expired sessions).</li>
                <li><span className="font-medium">Personal data (name/email/booking ref)</span>: anonymized after <span className="font-medium">6 months</span> (completed/expired sessions).</li>
                <li><span className="font-medium">Webhook logs</span>: purged after <span className="font-medium">90 days</span>.</li>
                <li><span className="font-medium">Active/unstarted sessions</span>: never touched by automated cleanup.</li>
                <li><span className="font-medium">Right to erasure</span>: immediate deletion on request unless a legal obligation applies.</li>
              </ul>
            </div>

            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cleanup Logs</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600">
                      <th className="py-2 pr-4">Time</th>
                      <th className="py-2 pr-4">Type</th>
                      <th className="py-2 pr-4">Duration (ms)</th>
                      <th className="py-2 pr-4">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsLoading ? (
                      <tr><td className="py-4 text-gray-500" colSpan={4}>Loading...</td></tr>
                    ) : gdprLogs.length === 0 ? (
                      <tr><td className="py-4 text-gray-500" colSpan={4}>No logs found</td></tr>
                    ) : (
                      gdprLogs.map((log) => (
                        <tr key={log.id} className="border-t">
                          <td className="py-2 pr-4">{new Date(log.created_at).toLocaleString()}</td>
                          <td className="py-2 pr-4">{log.type || log.cleanup_type}</td>
                          <td className="py-2 pr-4">{log.duration || log.processing_time || 0}</td>
                          <td className="py-2 pr-4"><code className="text-xs text-gray-600">{typeof log.result === 'object' ? JSON.stringify(log.result) : (log.result || log.status || '')}</code></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'admins' && (
          <div className="space-y-6">
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Admin</h3>
              <form onSubmit={handleCreateAdmin} className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={newAdmin.email} onChange={(e)=>setNewAdmin({...newAdmin, email: e.target.value})} className="input-field" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input type="password" value={newAdmin.password} onChange={(e)=>setNewAdmin({...newAdmin, password: e.target.value})} className="input-field" required />
                </div>
                <div className="flex items-center space-x-2">
                  <input id="is_superadmin" type="checkbox" checked={newAdmin.is_superadmin} onChange={(e)=>setNewAdmin({...newAdmin, is_superadmin: e.target.checked})} />
                  <label htmlFor="is_superadmin" className="text-sm text-gray-700">Super Admin</label>
                </div>
                <div className="flex justify-start lg:justify-end">
                  <button type="submit" className="btn-primary">Create</button>
                </div>
              </form>
            </div>
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Users</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600">
                      <th className="py-2 pr-4">Email</th>
                      <th className="py-2 pr-4">Super Admin</th>
                      <th className="py-2 pr-4">Created</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.length === 0 ? (
                      <tr><td className="py-4 text-gray-500" colSpan={3}>No admins yet</td></tr>
                    ) : (
                      admins.map(a => (
                        <tr key={a.id} className="border-t">
                          <td className="py-2 pr-4">{a.email}</td>
                          <td className="py-2 pr-4">{a.is_superadmin ? 'Yes' : 'No'}</td>
                          <td className="py-2 pr-4">{new Date(a.created_at).toLocaleString()}</td>
                          <td className="py-2 pr-4 text-right">
                            <button
                              onClick={async () => {
                                const confirmed = window.confirm(`Delete admin ${a.email}?`)
                                if (!confirmed) return
                                try {
                                  const res = await authAPI.deleteAdmin(a.id)
                                  if (res.success) {
                                    toast.success('Admin deleted')
                                    loadAdmins()
                                  } else {
                                    toast.error(res.error || 'Failed to delete admin')
                                  }
                                } catch (e) { toast.error('Failed to delete admin') }
                              }}
                              className="btn-secondary text-xs"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'manual-codes' && (
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Manual Access Code</h3>
            <form onSubmit={handleManualCodeSubmit} className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hunt
                  </label>
                  <select
                    value={manualCodeForm.hunt_id}
                    onChange={(e) => setManualCodeForm({ ...manualCodeForm, hunt_id: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                    required
                  >
                    <option value="">Select a hunt</option>
                    {hunts.map((hunt) => (
                      <option key={hunt.id} value={hunt.id}>
                        {hunt.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Email
                  </label>
                  <input
                    type="email"
                    value={manualCodeForm.customer_email}
                    onChange={(e) => setManualCodeForm({ ...manualCodeForm, customer_email: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                    placeholder="customer@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={manualCodeForm.customer_name}
                    onChange={(e) => setManualCodeForm({ ...manualCodeForm, customer_name: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Access Code (optional)
                  </label>
                  <input
                    type="text"
                    value={manualCodeForm.access_code}
                    onChange={(e) => setManualCodeForm({ ...manualCodeForm, access_code: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                    placeholder="Leave empty to auto-generate"
                    maxLength={6}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Booking Reference (optional)
                  </label>
                  <input
                    type="text"
                    value={manualCodeForm.booking_reference}
                    onChange={(e) => setManualCodeForm({ ...manualCodeForm, booking_reference: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                    placeholder="GYG123456"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={creatingCode}
                  className="btn-primary flex items-center"
                >
                  {creatingCode ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Code
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'completion-email' && (
          <div className="space-y-6">
            {/* Important Notice */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 p-4 rounded-lg">
              <div className="flex items-start">
                <Trophy className="w-5 h-5 text-purple-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-semibold text-purple-900 mb-1">The Circle Membership Card</h4>
                  <p className="text-sm text-purple-800 mb-2">
                    Certificates are now automatically generated as premium membership cards for "The Circle" - a branded, dark-themed card design featuring:
                  </p>
                  <ul className="text-sm text-purple-700 space-y-1 ml-4 list-disc">
                    <li>User's name and completed hunt details</li>
                    <li>Completion time displayed prominently</li>
                    <li>Virtual prizes earned as badge pills</li>
                    <li>Unique member ID and join date</li>
                  </ul>
                  <p className="text-sm text-purple-800 mt-2">
                    <strong>The certificate design is hardcoded for brand consistency.</strong> This template only customizes the email wrapper (intro text, buttons, footer).
                  </p>
                </div>
              </div>
            </div>

            {/* Completion Email Template Editor */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Email Wrapper Template</h3>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={testCompletionEmail}
                    className="btn-secondary text-sm flex items-center"
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Send Test Email
                  </button>
                </div>
              </div>

              <form onSubmit={handleCompletionEmailSettingsSubmit} className="space-y-4">
                {/* Subject Line */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Subject Line
                  </label>
                  <input
                    type="text"
                    value={completionEmailSettings.subject}
                    onChange={(e) => setCompletionEmailSettings({ ...completionEmailSettings, subject: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                    placeholder="🏆 Congratulations! Your {hunt_name} Certificate is Ready"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Available variables: {'{hunt_name}, {user_name}, {city}, {certificate_link}, {certificate_expires_at}'}
                  </p>
                </div>

                {/* Template Variables */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Template Variables</h4>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                    <div className="bg-purple-100 px-2 py-1 rounded border border-purple-300">
                      <code className="text-purple-800 font-semibold">{'{inline_certificate}'}</code>
                      <div className="text-purple-600 font-medium">🏆 The Circle Card (auto)</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{hunt_name}'}</code>
                      <div className="text-blue-600">Hunt name</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{user_name}'}</code>
                      <div className="text-blue-600">User name</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{city}'}</code>
                      <div className="text-blue-600">City</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{user_email}'}</code>
                      <div className="text-blue-600">User email</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{certificate_token}'}</code>
                      <div className="text-blue-600">Certificate ID</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{completed_at}'}</code>
                      <div className="text-blue-600">Completion date</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{YEAR}'}</code>
                      <div className="text-blue-600">Current year</div>
                    </div>
                  </div>
                  <p className="text-xs text-blue-700 mt-2">
                    <strong>Important:</strong> Use <code className="bg-blue-200 px-1 rounded">{'{inline_certificate}'}</code> to insert The Circle membership card. All other variables are for the email wrapper text.
                  </p>
                </div>

                {/* Email Body Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Wrapper Template (HTML)
                  </label>
                  <p className="text-xs text-gray-600 mb-2">
                    This HTML wraps around The Circle membership card. Include <code className="bg-gray-200 px-1 rounded">{'{inline_certificate}'}</code> where you want the card to appear.
                  </p>
                  <textarea
                    value={completionEmailSettings.body}
                    onChange={(e) => setCompletionEmailSettings({ ...completionEmailSettings, body: e.target.value })}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none font-mono text-sm"
                    placeholder="Enter HTML email template with {inline_certificate} placeholder..."
                  />
                </div>

                {/* Text Body Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plain Text Version (Fallback)
                  </label>
                  <p className="text-xs text-gray-600 mb-2">
                    Plain text version for email clients that don't support HTML. The certificate won't display in plain text.
                  </p>
                  <textarea
                    value={completionEmailSettings.text_body}
                    onChange={(e) => setCompletionEmailSettings({ ...completionEmailSettings, text_body: e.target.value })}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none font-mono text-sm"
                    placeholder="Enter plain text version..."
                  />
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={savingCompletionEmail}
                    className="btn-primary flex items-center"
                  >
                    {savingCompletionEmail ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Completion Email Template
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* SendGrid API Configuration for Completion Emails */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">SendGrid API Configuration</h3>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${emailSettings.sendgrid_api_key ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="text-sm text-gray-600">
                      {emailSettings.sendgrid_api_key ? 'API Key Configured' : 'API Key Missing'}
                    </span>
                  </div>
                </div>
              </div>

              <form onSubmit={handleEmailSettingsSubmit} className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SendGrid API Key
                    </label>
                    <div className="relative">
                      <input
                        type="password"
                        value={emailSettings.sendgrid_api_key || ''}
                        onChange={(e) => setEmailSettings({ ...emailSettings, sendgrid_api_key: e.target.value })}
                        className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                        placeholder="SG.xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Your SendGrid API key for sending completion emails
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sender Email Address
                    </label>
                    <input
                      type="email"
                      value={emailSettings.from_email || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
                      className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                      placeholder="noreply@yourdomain.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Email address that will appear as the sender
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sender Name
                  </label>
                  <input
                    type="text"
                    value={emailSettings.from_name || ''}
                    onChange={(e) => setEmailSettings({ ...emailSettings, from_name: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                    placeholder="Secrets of the Path"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Name that will appear as the sender
                  </p>
                </div>
                
                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={testEmailConfiguration}
                    className="btn-secondary flex items-center"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Test Email Configuration
                  </button>
                  <button
                    type="button"
                    onClick={handleEmailSettingsSubmit}
                    disabled={savingEmail}
                    className="btn-primary flex items-center"
                  >
                    {savingEmail ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Configuration
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'email' && (
          <div className="space-y-6">
            {/* Email Configuration Card */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Configuration</h3>
              <form onSubmit={handleEmailSettingsSubmit} className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SendGrid API Key
                    </label>
                    <input
                      type="password"
                      value={emailSettings.sendgrid_api_key}
                      onChange={(e) => setEmailSettings({ ...emailSettings, sendgrid_api_key: e.target.value })}
                      className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                      placeholder="SG.xxxxxxxxxxxxxxxxxxxxx"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      From Email
                    </label>
                    <input
                      type="email"
                      value={emailSettings.from_email}
                      onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
                      className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                      placeholder="noreply@yourcompany.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      From Name
                    </label>
                    <input
                      type="text"
                      value={emailSettings.from_name}
                      onChange={(e) => setEmailSettings({ ...emailSettings, from_name: e.target.value })}
                      className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                      placeholder="Your Company Name"
                    />
                  </div>
                </div>
                
                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={testEmailConfiguration}
                    className="btn-secondary flex items-center"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Test Email Configuration
                  </button>
                  <button
                    type="button"
                    onClick={handleEmailSettingsSubmit}
                    disabled={savingEmail}
                    className="btn-primary flex items-center"
                  >
                    {savingEmail ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Configuration
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Advanced Email Template Editor */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Advanced Email Template Editor</h3>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setEmailSettings(prev => ({ ...prev, email_template_body: getDefaultTemplate() }))}
                    className="btn-secondary text-sm flex items-center"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    Load Default Template
                  </button>
                  <button
                    type="button"
                    onClick={testEmailTemplate}
                    className="btn-secondary text-sm flex items-center"
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Send Template Test
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {/* Subject Line */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Subject Line
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={emailSettings.email_template_subject}
                      onChange={(e) => setEmailSettings({ ...emailSettings, email_template_subject: e.target.value })}
                      className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                      placeholder="Your Scavenger Hunt Access Code - {hunt_name}"
                    />
                    <div className="absolute right-2 top-2">
                      <span className="text-xs text-gray-400">
                        {emailSettings.email_template_subject?.length || 0}/60
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Available variables: {'{hunt_name}, {customer_name}, {access_code}'}
                  </p>
                </div>

                {/* Template Variables */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Template Variables</h4>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{hunt_name}'}</code>
                      <div className="text-blue-600">Hunt name</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{customer_name}'}</code>
                      <div className="text-blue-600">Customer name</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{access_code}'}</code>
                      <div className="text-blue-600">Access code</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{booking_reference}'}</code>
                      <div className="text-blue-600">Booking ref</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{hunt_url}'}</code>
                      <div className="text-blue-600">Hunt URL</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{estimated_duration}'}</code>
                      <div className="text-blue-600">Duration</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{difficulty}'}</code>
                      <div className="text-blue-600">Difficulty</div>
                    </div>
                    <div className="bg-blue-100 px-2 py-1 rounded">
                      <code className="text-blue-800">{'{expires_in}'}</code>
                      <div className="text-blue-600">Expires in</div>
                    </div>
                  </div>
                </div>

                {/* Email Body Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Body Template
                  </label>
                  <div className="border border-gray-300 rounded-md overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 border-b border-gray-300 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => insertVariable('{customer_name}')}
                          className="text-xs bg-white px-3 py-2 rounded border hover:bg-gray-50 transition-colors"
                        >
                          Customer Name
                        </button>
                        <button
                          type="button"
                          onClick={() => insertVariable('{hunt_name}')}
                          className="text-xs bg-white px-3 py-2 rounded border hover:bg-gray-50 transition-colors"
                        >
                          Hunt Name
                        </button>
                        <button
                          type="button"
                          onClick={() => insertVariable('{access_code}')}
                          className="text-xs bg-white px-3 py-2 rounded border hover:bg-gray-50 transition-colors"
                        >
                          Access Code
                        </button>
                        <button
                          type="button"
                          onClick={() => insertVariable('{hunt_url}')}
                          className="text-xs bg-white px-3 py-2 rounded border hover:bg-gray-50 transition-colors"
                        >
                          Hunt URL
                        </button>
                      </div>
                      <div className="text-xs text-gray-500">
                        {emailSettings.email_template_body?.length || 0} characters
                      </div>
                    </div>
                    <textarea
                      value={emailSettings.email_template_body}
                      onChange={(e) => setEmailSettings({ ...emailSettings, email_template_body: e.target.value })}
                      rows={12}
                      className="w-full px-3 py-2 border-0 focus:outline-none focus:ring-0 resize-none"
                      placeholder="Hi {customer_name},&#10;&#10;Your access code for {hunt_name} is: {access_code}&#10;&#10;Enjoy your adventure!"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Use the buttons above to insert variables, or type them manually
                  </p>
                </div>

                {/* Template Actions */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                    <button
                      type="button"
                      onClick={() => setEmailSettings(prev => ({ ...prev, email_template_body: getSimpleTemplate() }))}
                      className="btn-secondary text-sm py-2 px-4"
                    >
                      Simple Template
                    </button>
                    <button
                      type="button"
                      onClick={() => setEmailSettings(prev => ({ ...prev, email_template_body: getAdvancedTemplate() }))}
                      className="btn-secondary text-sm py-2 px-4"
                    >
                      Advanced Template
                    </button>
                  </div>
                  <button
                    type="submit"
                    disabled={savingEmail}
                    className="btn-primary flex items-center"
                  >
                    {savingEmail ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving Template...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Email Template
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Certificate Test Tab */}
        {activeTab === 'certificate-test' && (
          <div className="space-y-6">
            {/* Certificate Test Section */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Certificate Test</h3>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${testCertificateResult ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span className="text-sm text-gray-600">
                      {testCertificateResult ? 'Test Certificate Ready' : 'No Test Certificate'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {/* Test Data Configuration */}
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          User Name
                        </label>
                        <input
                          type="text"
                          value={testCertificateData.userName}
                          onChange={(e) => setTestCertificateData({ ...testCertificateData, userName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Test User"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          User Email
                        </label>
                        <input
                          type="email"
                          value={testCertificateData.userEmail}
                          onChange={(e) => setTestCertificateData({ ...testCertificateData, userEmail: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="test@example.com"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hunt Name
                        </label>
                        <input
                          type="text"
                          value={testCertificateData.huntName}
                          onChange={(e) => setTestCertificateData({ ...testCertificateData, huntName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Central Park Classic"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          City
                        </label>
                        <input
                          type="text"
                          value={testCertificateData.city}
                          onChange={(e) => setTestCertificateData({ ...testCertificateData, city: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="New York"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Hunt Performance */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">Hunt Performance</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Total Attempts
                        </label>
                        <input
                          type="number"
                          value={testCertificateData.totalAttempts}
                          onChange={(e) => setTestCertificateData({ ...testCertificateData, totalAttempts: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="12"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Locations Visited
                        </label>
                        <input
                          type="number"
                          value={testCertificateData.totalLocationsVisited}
                          onChange={(e) => setTestCertificateData({ ...testCertificateData, totalLocationsVisited: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="10"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Total User Completions
                        </label>
                        <input
                          type="number"
                          value={testCertificateData.totalUserCompletions}
                          onChange={(e) => setTestCertificateData({ ...testCertificateData, totalUserCompletions: parseInt(e.target.value) || 1 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Timing Information */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">Timing Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Completion Hour (0-23)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="23"
                          value={testCertificateData.completionHour}
                          onChange={(e) => setTestCertificateData({ ...testCertificateData, completionHour: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="14"
                        />
                        <p className="text-xs text-gray-500 mt-1">Hour when hunt was completed (24-hour format)</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Completion Day (0-6)
                        </label>
                        <select
                          value={testCertificateData.completionDay}
                          onChange={(e) => setTestCertificateData({ ...testCertificateData, completionDay: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value={0}>Sunday</option>
                          <option value={1}>Monday</option>
                          <option value={2}>Tuesday</option>
                          <option value={3}>Wednesday</option>
                          <option value={4}>Thursday</option>
                          <option value={5}>Friday</option>
                          <option value={6}>Saturday</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Day of the week when hunt was completed</p>
                      </div>
                    </div>
                  </div>

                  {/* Virtual Prize Scenarios */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">Virtual Prize Scenarios</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={testCertificateData.isSpeedDemon}
                            onChange={(e) => setTestCertificateData({ ...testCertificateData, isSpeedDemon: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Speed Demon (Under 1 hour)</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={testCertificateData.isPerfectScore}
                            onChange={(e) => setTestCertificateData({ ...testCertificateData, isPerfectScore: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Perfect Score (No hints needed)</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={testCertificateData.isNightOwl}
                            onChange={(e) => setTestCertificateData({ ...testCertificateData, isNightOwl: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Night Owl (After 6 PM)</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={testCertificateData.isWeekendWarrior}
                            onChange={(e) => setTestCertificateData({ ...testCertificateData, isWeekendWarrior: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Weekend Warrior</span>
                        </label>
                      </div>
                      
                      <div className="space-y-3">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={testCertificateData.isEarlyBird}
                            onChange={(e) => setTestCertificateData({ ...testCertificateData, isEarlyBird: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Early Bird (Before 10 AM)</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={testCertificateData.isStreakMaster}
                            onChange={(e) => setTestCertificateData({ ...testCertificateData, isStreakMaster: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Streak Master (3+ hunts)</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={testCertificateData.isExplorerLegend}
                            onChange={(e) => setTestCertificateData({ ...testCertificateData, isExplorerLegend: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Explorer Legend (10+ hunts)</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Quick Presets */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">Quick Test Presets</h4>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setTestCertificateData({
                          ...testCertificateData,
                          totalAttempts: 10,
                          totalLocationsVisited: 10,
                          isPerfectScore: true,
                          isSpeedDemon: false,
                          isNightOwl: false,
                          isWeekendWarrior: false,
                          isEarlyBird: false,
                          isStreakMaster: false,
                          isExplorerLegend: false
                        })}
                        className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded-full hover:bg-green-200 transition-colors"
                      >
                        Perfect Score
                      </button>
                      <button
                        onClick={() => setTestCertificateData({
                          ...testCertificateData,
                          totalAttempts: 8,
                          totalLocationsVisited: 10,
                          isPerfectScore: false,
                          isSpeedDemon: true,
                          isNightOwl: false,
                          isWeekendWarrior: false,
                          isEarlyBird: false,
                          isStreakMaster: false,
                          isExplorerLegend: false
                        })}
                        className="px-3 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full hover:bg-yellow-200 transition-colors"
                      >
                        Speed Demon
                      </button>
                      <button
                        onClick={() => setTestCertificateData({
                          ...testCertificateData,
                          completionHour: 20,
                          completionDay: 6,
                          isPerfectScore: false,
                          isSpeedDemon: false,
                          isNightOwl: true,
                          isWeekendWarrior: true,
                          isEarlyBird: false,
                          isStreakMaster: false,
                          isExplorerLegend: false
                        })}
                        className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200 transition-colors"
                      >
                        Weekend Night Owl
                      </button>
                      <button
                        onClick={() => setTestCertificateData({
                          ...testCertificateData,
                          totalUserCompletions: 10,
                          isPerfectScore: false,
                          isSpeedDemon: false,
                          isNightOwl: false,
                          isWeekendWarrior: false,
                          isEarlyBird: false,
                          isStreakMaster: false,
                          isExplorerLegend: true
                        })}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded-full hover:bg-blue-200 transition-colors"
                      >
                        Explorer Legend
                      </button>
                      <button
                        onClick={() => setTestCertificateData({
                          ...testCertificateData,
                          totalAttempts: 15,
                          totalLocationsVisited: 10,
                          isPerfectScore: false,
                          isSpeedDemon: false,
                          isNightOwl: false,
                          isWeekendWarrior: false,
                          isEarlyBird: false,
                          isStreakMaster: false,
                          isExplorerLegend: false
                        })}
                        className="px-3 py-1 text-xs bg-gray-100 text-gray-800 rounded-full hover:bg-gray-200 transition-colors"
                      >
                        No Prizes
                      </button>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={previewCertificate}
                    disabled={certificatePreviewLoading || !testCertificateData.userName || !testCertificateData.huntName || !testCertificateData.city}
                    className="btn-secondary flex items-center"
                  >
                    {certificatePreviewLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                        Loading...
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-2" />
                        Preview Certificate
                      </>
                    )}
                  </button>
                  <button
                    onClick={testCertificate}
                    disabled={testCertificateLoading}
                    className="btn-primary flex items-center"
                  >
                    {testCertificateLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Trophy className="w-4 h-4 mr-2" />
                        Generate Test Certificate
                      </>
                    )}
                  </button>
                </div>

                {/* Test Results */}
                {testCertificateResult && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-green-900 mb-3">Test Certificate Generated Successfully!</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-green-700">Certificate Token:</span>
                        <code className="text-xs bg-green-100 px-2 py-1 rounded">{testCertificateResult.certificateToken}</code>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-green-700">Certificate Link:</span>
                        <code className="text-xs bg-green-100 px-2 py-1 rounded break-all">{testCertificateResult.certificateLink}</code>
                      </div>
                    </div>
                    
                    <div className="flex space-x-3 mt-4">
                      <button
                        onClick={viewTestCertificate}
                        className="btn-secondary flex items-center text-sm"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        View Certificate
                      </button>
                      <button
                        onClick={viewTestEmail}
                        className="btn-secondary flex items-center text-sm"
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        View Email
                      </button>
                    </div>
                  </div>
                )}

                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">How to Test</h4>
                  <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                    <li>Configure the test data above (user name, hunt name, city)</li>
                    <li>Click "Generate Test Certificate" to create a test certificate</li>
                    <li>Click "View Certificate" to see what users see when they click the certificate link</li>
                    <li>Click "View Email" to see the completion email with the certificate link</li>
                    <li>This simulates the complete user experience from email to certificate</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'system' && (
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">System Configuration</h3>
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-yellow-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      System Settings
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>System configuration options will be available here.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Certificate Preview Modal */}
      <CertificatePreviewModal
        isOpen={certificatePreviewOpen}
        onClose={() => setCertificatePreviewOpen(false)}
        certificateHTML={certificatePreviewHTML}
        certificateLink={testCertificateResult?.certificateLink}
        mockPrizes={certificatePreviewPrizes}
      />
    </div>
  )
}

export default Settings
